import { useState } from "react";
import "./Login.css";

export default function Login({ isSignup: isSignupDefault = true, onClose }) {
  const [isSignup, setIsSignup] = useState(isSignupDefault);
  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Basic validation
    if (isSignup) {
      const { displayName, email, password, confirmPassword } = formData;
      if (!displayName || !email || !password || !confirmPassword) {
        setError("Please fill in all fields");
        setIsLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match");
        setIsLoading(false);
        return;
      }
      if (password.length < 6) {
        setError("Password must be at least 6 characters");
        setIsLoading(false);
        return;
      }
    } else {
      const { email, password } = formData;
      if (!email || !password) {
        setError("Please fill in all fields");
        setIsLoading(false);
        return;
      }
    }

    try {
      if (isSignup) {
        await fakeAuthApiSignup(formData.email, formData.password, formData.displayName);
      } else {
        await fakeAuthApiLogin(formData.email, formData.password);
      }

      // Mark success so the UI can flash a message
      setCreated(true);

      // Pull user from storage (signup stored it; login may need to seed it)
      const stored = JSON.parse(localStorage.getItem("tg.userStats") || "null");
      const user = stored?.displayName
        ? stored
        : seedUserIfMissing(formData); // ensures we always have a user object

      // Brief success flash, then notify parent with the user object
      setTimeout(() => {
        onClose?.(user);
      }, 700);
    } catch (err) {
      setError(err?.message || (isSignup ? "Failed to create account" : "Failed to sign in"));
    } finally {
      setIsLoading(false);
    }
  };

  // Ensures a basic user record exists even after "login" flow
  function seedUserIfMissing({ displayName, email }) {
    const fallback = {
      displayName: displayName || "Explorer",
      photosTaken: 0,
      successfulMatches: 0,
      currentStreak: 0,
      bestStreak: 0,
      completedObjectives: [],
      joinDate: new Date().toLocaleDateString(),
      email: email || "",
    };
    localStorage.setItem("tg.userStats", JSON.stringify(fallback));
    return fallback;
  }

  return (
    <div className="page">
      <div className="intro">
        <div className="emoji">🌿</div>
        <h2 className="title">{isSignup ? "Create your account" : "Welcome back!"}</h2>
        <p className="hint">
          {isSignup ? (
            <>
              Already have an account?{" "}
              <button type="button" className="link" onClick={() => setIsSignup(false)}>
                Sign in here
              </button>
            </>
          ) : (
            <>
              Need an account?{" "}
              <button type="button" className="link" onClick={() => setIsSignup(true)}>
                Sign up here
              </button>
            </>
          )}
        </p>
      </div>

      <div className="card">
        <form className="form" onSubmit={handleSubmit} noValidate>
          {error && <div className="alert">{error}</div>}
          {created && (
            <div className="success">
              {isSignup ? "Account created! 🎉" : "Signed in! 🌿"}
            </div>
          )}

          {isSignup && (
            <label className="field">
              <span className="label">Display Name</span>
              <input
                id="displayName"
                name="displayName"
                type="text"
                autoComplete="name"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Your explorer name"
                required
                className="input"
              />
            </label>
          )}

          <label className="field">
            <span className="label">Email</span>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
              className="input"
            />
          </label>

          <label className="field">
            <span className="label">Password</span>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete={isSignup ? "new-password" : "current-password"}
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              required
              className="input"
            />
            {isSignup && <span className="help">Must be at least 6 characters</span>}
          </label>

          {isSignup && (
            <label className="field">
              <span className="label">Confirm password</span>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="input"
              />
            </label>
          )}

          <button type="submit" className="btn" disabled={isLoading}>
            {isLoading
              ? isSignup
                ? "Creating account..."
                : "Signing in..."
              : isSignup
              ? "Sign up"
              : "Sign in"}
          </button>
        </form>

        {isSignup && (
          <div className="terms">
            <div className="rule"><span>Terms</span></div>
            <p className="tos">
              By signing up, you agree to our terms of service and privacy policy.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ======= Demo API mocks (replace with real calls) ======= */
function fakeAuthApiSignup(email, password, displayName) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (email.endsWith("@example.com")) {
        reject(new Error("Email already in use"));
      } else {
        const userData = {
          displayName: displayName || "Explorer",
          photosTaken: 0,
          successfulMatches: 0,
          currentStreak: 0,
          bestStreak: 0,
          completedObjectives: [],
          joinDate: new Date().toLocaleDateString(),
          email,
        };
        localStorage.setItem("tg.userStats", JSON.stringify(userData));
        resolve({ ok: true });
      }
    }, 700);
  });
}

function fakeAuthApiLogin(email, password) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      // very basic demo credential
      if (email === "test@test.com" && password === "password") {
        // If no user in storage, seed a minimal one so Header has something to show
        const existing = JSON.parse(localStorage.getItem("tg.userStats") || "null");
        if (!existing) {
          const userData = {
            displayName: "Explorer",
            photosTaken: 0,
            successfulMatches: 0,
            currentStreak: 0,
            bestStreak: 0,
            completedObjectives: [],
            joinDate: new Date().toLocaleDateString(),
            email,
          };
          localStorage.setItem("tg.userStats", JSON.stringify(userData));
        }
        resolve({ ok: true });
      } else {
        reject(new Error("Invalid email or password"));
      }
    }, 700);
  });
}
